import { useEffect, useState } from 'react';
import { AlertCircle, Download, FileQuestion, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { getErrorMessage } from '@/lib/error';
import type { ComposeAttachment } from '@/types/compose';
import { isPreviewableMimeType } from '@/features/email-detail/hooks/useInlineAttachments';
import { formatFileSize, renderAttachmentPreview } from '@/features/email-detail/utils/attachmentPreview';
import { getComposeAttachmentBlob } from '../utils/composeAttachmentBlob';

interface ComposeAttachmentPreviewDialogProps {
  attachment: ComposeAttachment | null;
  onOpenChange: (open: boolean) => void;
}

type LoadState =
  | { status: 'loading' }
  | { status: 'ready'; url: string; mimeType: string }
  | { status: 'error'; message: string };

/**
 * Lets the sender confirm an attached file is the right one before sending —
 * renders it with the same previewers the read view uses.
 */
export function ComposeAttachmentPreviewDialog({ attachment, onOpenChange }: ComposeAttachmentPreviewDialogProps) {
  const [state, setState] = useState<LoadState>({ status: 'loading' });

  useEffect(() => {
    if (!attachment) {
      return;
    }
    let cancelled = false;
    let objectUrl: string | null = null;
    setState({ status: 'loading' });
    getComposeAttachmentBlob(attachment, { preview: true })
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setState({ status: 'ready', url: objectUrl, mimeType: attachment.mimeType || blob.type });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        console.error('Failed to load attachment preview', error);
        setState({ status: 'error', message: getErrorMessage(error) ?? 'Failed to load attachment' });
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    // Re-run only when a different attachment is opened — the parent re-creates
    // attachment objects on every draft save.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachment?.id]);

  const previewNode =
    attachment && state.status === 'ready' && isPreviewableMimeType(state.mimeType)
      ? renderAttachmentPreview({ mimeType: state.mimeType, filename: attachment.name }, state.url)
      : null;

  return (
    <Dialog open={attachment !== null} onOpenChange={onOpenChange}>
      <DialogContent
        // Escape closes only the preview — without this the global
        // compose:discard shortcut also sees the key and closes the composer.
        onEscapeKeyDown={(event) => event.stopPropagation()}
        className="flex h-[85vh] w-[calc(100vw-2rem)] max-w-4xl flex-col gap-3 overflow-hidden sm:max-w-4xl"
      >
        <DialogHeader className="pr-8">
          <DialogTitle className="truncate text-base">{attachment?.name ?? 'Attachment'}</DialogTitle>
          <DialogDescription>{attachment ? formatFileSize(attachment.size) : null}</DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-border/60 bg-muted/20 p-3">
          {state.status === 'loading' && (
            <div className="flex h-full items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}
          {state.status === 'error' && (
            <div className="flex h-full flex-col items-center justify-center gap-2 text-sm text-destructive">
              <AlertCircle className="h-6 w-6" />
              <span>{state.message}</span>
            </div>
          )}
          {state.status === 'ready' && previewNode}
          {state.status === 'ready' && !previewNode && (
            <div className="flex h-full flex-col items-center justify-center gap-2 text-sm text-muted-foreground">
              <FileQuestion className="h-6 w-6" />
              <span>No preview for this file type — download it to check.</span>
            </div>
          )}
        </div>

        <DialogFooter>
          {state.status === 'ready' ? (
            <Button variant="outline" size="sm" className="gap-2" asChild>
              <a href={state.url} download={attachment?.name || 'attachment'}>
                <Download className="h-4 w-4" />
                Download
              </a>
            </Button>
          ) : (
            <Button variant="outline" size="sm" className="gap-2" disabled>
              <Download className="h-4 w-4" />
              Download
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
